/**
 * Fragmentador prompt contract — keep in sync with supabase/functions/_shared/fragmentadorPromptContract.ts
 */

export const CAMERA_MOVE_IDS = [
  "static",
  "dolly_in",
  "dolly_out",
  "pan_left",
  "pan_right",
  "tilt_up",
  "tilt_down",
  "orbit_left",
  "orbit_right",
  "tracking",
  "crane_down",
  "crane",
  "steadicam",
  "shake",
  "rotate_90",
  "forward",
] as const;

export type CameraMoveId = (typeof CAMERA_MOVE_IDS)[number];

export const CAMERA_MOVE_EN: Record<CameraMoveId, string> = {
  static: "locked static camera, no pan or dolly",
  dolly_in: "push forward toward the subject",
  dolly_out: "pull back revealing wider context",
  pan_left: "pan left in a lateral sweep",
  pan_right: "pan right in a lateral sweep",
  tilt_up: "tilt up revealing height",
  tilt_down: "tilt down toward ground detail",
  orbit_left: "arc counter-clockwise around the subject",
  orbit_right: "arc clockwise around the subject",
  tracking: "smooth tracking shot following the subject",
  crane: "crane rise combining vertical and forward motion",
  crane_down: "descending crane / drone lowering toward the subject while moving forward",
  steadicam: "continuous steadicam glide",
  shake: "subtle handheld camera shake for urgency",
  rotate_90: "camera rotates about ninety degrees around the subject",
  forward: "fly straight forward along the path",
};

export const CAMERA_MOVE_PT: Record<CameraMoveId, string> = {
  static: "Câmera estática",
  dolly_in: "Avanço (dolly in)",
  dolly_out: "Recuo (dolly out)",
  pan_left: "Panorama esquerda",
  pan_right: "Panorama direita",
  tilt_up: "Inclinação para cima",
  tilt_down: "Inclinação para baixo",
  orbit_left: "Órbita esquerda",
  orbit_right: "Órbita direita",
  tracking: "Travelling",
  crane: "Grua",
  crane_down: "Grua descendo",
  steadicam: "Steadicam",
  shake: "Camera shake",
  rotate_90: "Rotação 90°",
  forward: "Movimento reto",
};

const CAMERA_ALIASES: Record<string, CameraMoveId> = {
  dolly: "dolly_in",
  push_in: "dolly_in",
  pull_back: "dolly_out",
  zoom_in: "dolly_in",
  zoom_out: "dolly_out",
  orbit: "orbit_right",
  rotate: "rotate_90",
  rotation: "rotate_90",
  "90": "rotate_90",
  handheld: "shake",
  shake_cam: "shake",
  straight: "forward",
  movimento_reto: "forward",
  descend: "crane_down",
  crane_descend: "crane_down",
  drone_down: "crane_down",
  grua_descendo: "crane_down",
  pan: "pan_right",
};

const STILL_CAMERA_VERBS =
  /\b(dolly|pan\b|tracking|crane|steadicam|orbit|handheld shake|camera shake|pushes? in|pulls? back|flies?|glides?)\b/i;

const SKETCH_STYLE =
  /\b(sketch|low[\s-]?poly|line art|wireframe|cartoon|anime|illustration|storyboard paper|pencil drawing)\b/i;
const NEGATED_SKETCH_STYLE =
  /\b(?:no|not|never|without|avoid|non)[\s-]+(?:an?\s+|any\s+)?(?:sketch(?:es|y)?|low[\s-]?poly|line art|wireframe|cartoon(?:ish|s)?|anime|illustrations?|storyboard paper|pencil drawings?)\b/gi;

/** Asks for a drawn look ("no sketch" and similar negations do not count). */
function asksSketchStyle(text: string): boolean {
  return SKETCH_STYLE.test(text.replace(NEGATED_SKETCH_STYLE, " "));
}

export const STILL_PROMPT_SUFFIX =
  " Photoreal cinematic still. Same place and subject as the reference photos — preserve identity, materials, colors and proportions. Only change framing/time as described. No sketch, no illustration, no low-poly.";

export const VIDEO_PROMPT_MIN = 100;
export const VIDEO_PROMPT_MAX = 180;
export const VIDEO_PROMPT_HARD_CAP = 512;
/**
 * Whole continuous prompt. create-video adds chronology and a final lock.
 * Each PoYo model has its own character cap; overflow is condensed there, not rejected.
 */
export const CONTINUOUS_PROMPT_MAX = 1500;
const CONTINUOUS_PHASE_MIN = 110;
export const STILL_PROMPT_MIN = 80;
export const STILL_PROMPT_MAX = 400;
export const DESCRIPTION_PT_MAX = 80;

/** Default seconds allocated per storyboard phase (drives total video duration). */
export const SECONDS_PER_PHASE = 2;
export const PHASE_COUNT_MIN = 2;
export const PHASE_COUNT_MAX = 10;

export function clampPhaseCount(n: unknown): number {
  const v = Math.round(Number(n) || PHASE_COUNT_MIN);
  return Math.max(PHASE_COUNT_MIN, Math.min(PHASE_COUNT_MAX, v));
}

/** Video duration derived from phase count, clamped to model limits. */
export function durationFromPhaseCount(
  phaseCount: number,
  durationMin = 4,
  durationMax = 30,
): number {
  const n = clampPhaseCount(phaseCount);
  const raw = n * SECONDS_PER_PHASE;
  return Math.max(durationMin, Math.min(durationMax, raw));
}

export type FragmentadorPhaseRole = "start" | "beat" | "end";

/** How this phase connects to the next one. Last phase ignores this. */
export const TRANSITION_IDS = ["camera", "cut"] as const;
export type TransitionOutId = (typeof TRANSITION_IDS)[number];

export const TRANSITION_PT: Record<TransitionOutId, string> = {
  camera: "Movimento de câmera",
  cut: "Corte",
};

export const TRANSITION_EN: Record<TransitionOutId, string> = {
  camera: "continue in one take with camera motion into the next beat",
  cut: "HARD CUT to the next beat — new framing, no morph or dissolve",
};

/** Playful pace for UI + video model timing language. */
export const PACE_IDS = ["slow", "normal", "fast", "impact"] as const;
export type PaceId = (typeof PACE_IDS)[number];

export const PACE_PT: Record<PaceId, string> = {
  slow: "Lenta",
  normal: "Normal",
  fast: "Rápida",
  impact: "Impacto",
};

export const PACE_EMOJI: Record<PaceId, string> = {
  slow: "🐢",
  normal: "▶️",
  fast: "⚡",
  impact: "💥",
};

export const PACE_EN: Record<PaceId, string> = {
  slow: "slow contemplative pacing, linger on details",
  normal: "natural steady pacing",
  fast: "brisk energetic pacing, snappy motion",
  impact: "high-impact punchy beat, sharp accent at the peak",
};

export const PACE_WEIGHT: Record<PaceId, number> = {
  slow: 1.45,
  normal: 1,
  fast: 0.75,
  impact: 0.55,
};

/** Subject energy / action level. */
export const ENERGY_IDS = ["calm", "normal", "action", "intense"] as const;
export type EnergyId = (typeof ENERGY_IDS)[number];

export const ENERGY_PT: Record<EnergyId, string> = {
  calm: "Calma",
  normal: "Equilíbrio",
  action: "Ação",
  intense: "Intensa",
};

export const ENERGY_EMOJI: Record<EnergyId, string> = {
  calm: "🌊",
  normal: "✨",
  action: "🏃",
  intense: "🔥",
};

export const ENERGY_EN: Record<EnergyId, string> = {
  calm: "quiet restrained subject motion",
  normal: "natural subject activity",
  action: "clear purposeful action in frame",
  intense: "high-energy dynamic action, urgency",
};

export type FragmentadorPhase = {
  id: string;
  label: string;
  role: FragmentadorPhaseRole;
  t_start: number;
  t_end: number;
  camera_move: CameraMoveId;
  /** Transition TO the next phase (ignored on last). */
  transition_out: TransitionOutId;
  pace: PaceId;
  energy: EnergyId;
  description_pt: string;
  still_prompt_en: string;
  video_prompt_en: string;
  image_index: number;
  /**
   * On adjust: 1-based index into the PREVIOUS plan whose still to keep.
   * Omit / null = generate a new still (except start/end photo anchors).
   */
  reuse_from_index?: number | null;
  /** What happens in frame during this phase (subjects, effects, graphics) — never camera motion. */
  action_en?: string;
  /** Exact strings that must appear on screen in this phase. */
  on_screen_text?: string[];
  /** Ids of plan.requirements this phase realizes. */
  requirement_ids?: string[];
};

/** One atomic thing the user asked for; every one must land in the plan. */
export type PlanRequirement = {
  id: string;
  text_pt: string;
  /** "global" or 1-based phase numbers. */
  scope: "global" | number[];
};

/** Defaults the user's requests may lift (text on screen, visible aircraft, drawn looks). */
export type PlanAllow = {
  text_graphics: boolean;
  aircraft: boolean;
  stylized: boolean;
};

export type FragmentadorPlan = {
  total_duration_seconds: number;
  look: "cinematic" | "realistic";
  intent_hint:
    | "camera_showcase"
    | "progressive_build"
    | "subject_evolution"
    | "cinematic_montage";
  phases: FragmentadorPhase[];
  requirements?: PlanRequirement[];
  style_en?: string;
  continuity_en?: string;
  avoid_en?: string;
  allow?: PlanAllow;
  /** User requests the coverage audit could not find in the plan. */
  uncovered?: string[];
};

/** Generous caps: create-video condenses the whole prompt to the model limit, so nothing is cut here in practice. */
export const ACTION_EN_MAX = 900;
export const ON_SCREEN_TEXT_MAX = 160;
export const ON_SCREEN_TEXT_ITEMS = 12;
export const STYLE_EN_MAX = 500;
export const CONTINUITY_EN_MAX = 500;
export const AVOID_EN_MAX = 500;
export const REQUIREMENTS_MAX = 40;

export function normalizeAllow(raw: unknown): PlanAllow {
  const o = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  return {
    text_graphics: o.text_graphics === true,
    aircraft: o.aircraft === true,
    stylized: o.stylized === true,
  };
}

export function normalizeRequirements(raw: unknown, phaseCount: number): PlanRequirement[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: PlanRequirement[] = [];
  for (const r of raw) {
    const o = (r && typeof r === "object" ? r : { text_pt: r }) as Record<string, unknown>;
    const text_pt = String(o.text_pt || o.text || "").replace(/\s+/g, " ").trim().slice(0, 600);
    if (!text_pt) continue;
    let id = String(o.id || "").trim().slice(0, 12) || `r${out.length + 1}`;
    if (seen.has(id)) id = `r${out.length + 1}_${seen.size}`;
    seen.add(id);
    const scopeList = (Array.isArray(o.scope) ? o.scope : [])
      .map((n) => Math.round(Number(n)))
      .filter((n) => Number.isFinite(n) && n >= 1 && n <= phaseCount);
    out.push({ id, text_pt, scope: scopeList.length ? Array.from(new Set(scopeList)) : "global" });
    if (out.length >= REQUIREMENTS_MAX) break;
  }
  return out;
}

function cleanLine(raw: unknown, max: number): string {
  return truncateAtBoundary(String(raw || ""), max);
}

function normalizeOnScreenText(raw: unknown): string[] {
  const list = Array.isArray(raw) ? raw : typeof raw === "string" && raw.trim() ? [raw] : [];
  return list
    .map((s) => String(s || "").replace(/\s+/g, " ").trim().slice(0, ON_SCREEN_TEXT_MAX))
    .filter(Boolean)
    .slice(0, ON_SCREEN_TEXT_ITEMS);
}

export function normalizeTransitionOut(raw: unknown): TransitionOutId {
  const s = String(raw || "").trim().toLowerCase();
  if (s === "cut" || s === "hard_cut" || s === "corte" || s === "jump") return "cut";
  return "camera";
}

export function normalizePace(raw: unknown): PaceId {
  const s = String(raw || "").trim().toLowerCase();
  if ((PACE_IDS as readonly string[]).includes(s)) return s as PaceId;
  if (/slow|lenta|calm|slowmo/.test(s)) return "slow";
  if (/impact|punch|hit|impacto/.test(s)) return "impact";
  if (/fast|rápid|rapid|quick|snappy/.test(s)) return "fast";
  return "normal";
}

export function normalizeEnergy(raw: unknown): EnergyId {
  const s = String(raw || "").trim().toLowerCase();
  if ((ENERGY_IDS as readonly string[]).includes(s)) return s as EnergyId;
  if (/calm|quiet|suave|zen/.test(s)) return "calm";
  if (/intense|urgent|wild|intensa/.test(s)) return "intense";
  if (/action|ação|acao|dynamic|energia/.test(s)) return "action";
  return "normal";
}

export function expandCameraMove(id: CameraMoveId): string {
  return CAMERA_MOVE_EN[id] || CAMERA_MOVE_EN.static;
}

export function expandPace(id: PaceId): string {
  return PACE_EN[id] || PACE_EN.normal;
}

export function expandEnergy(id: EnergyId): string {
  return ENERGY_EN[id] || ENERGY_EN.normal;
}

export function expandTransitionOut(id: TransitionOutId): string {
  return TRANSITION_EN[id] || TRANSITION_EN.camera;
}

export function normalizeCameraMove(raw: unknown): CameraMoveId {
  const s = String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  if ((CAMERA_MOVE_IDS as readonly string[]).includes(s)) return s as CameraMoveId;
  if (CAMERA_ALIASES[s]) return CAMERA_ALIASES[s];
  for (const id of CAMERA_MOVE_IDS) {
    if (s.includes(id)) return id;
  }
  if (/shake|handheld/.test(s)) return "shake";
  if (/90|rotat/.test(s)) return "rotate_90";
  if (/orbit/.test(s)) return "orbit_right";
  if (/dolly.?in|push/.test(s)) return "dolly_in";
  if (/dolly.?out|pull/.test(s)) return "dolly_out";
  if (/forward|straight|reto/.test(s)) return "forward";
  return "steadicam";
}

export function truncateText(text: string, max: number): string {
  const t = String(text || "").trim();
  if (t.length <= max) return t;
  return t.slice(0, Math.max(0, max - 1)).trimEnd() + "…";
}

/** Cut at the last full sentence (or word) that fits — never mid-word, no ellipsis sent to the model. */
export function truncateAtBoundary(text: string, max: number): string {
  const t = String(text || "").replace(/\s+/g, " ").trim();
  if (t.length <= max) return t;
  const head = t.slice(0, max);
  const sentenceEnd = Math.max(head.lastIndexOf(". "), head.lastIndexOf("! "), head.lastIndexOf("? "));
  if (sentenceEnd >= max * 0.6) return head.slice(0, sentenceEnd + 1).trim();
  const space = head.lastIndexOf(" ");
  return (space > 0 ? head.slice(0, space) : head).replace(/[,;:\s—-]+$/, "").trim();
}

/** Per-phase video prompt budget so the continuous prompt (all phases) stays under ~2200 chars. */
export function phaseVideoPromptBudget(phaseCount: number): number {
  const n = Math.max(1, Math.round(phaseCount) || 1);
  return Math.max(VIDEO_PROMPT_MAX, Math.min(VIDEO_PROMPT_HARD_CAP, Math.floor(1500 / n)));
}

function ensureCite(prompt: string, imageIndex: number): string {
  let text = String(prompt || "").replace(/@image_(\d+)/gi, "<<<image_$1>>>");
  const token = `<<<image_${imageIndex}>>>`;
  if (!text.includes(token)) {
    text = `Match the look of ${token}. ${text}`.trim();
  }
  return text;
}

function ensureStartEndCites(prompt: string, imageIndex: number, phaseCount: number): string {
  let text = ensureCite(prompt, imageIndex);
  if (imageIndex === 1 && !/start exactly (?:on|from) <<<image_1>>>/i.test(text)) {
    text = `Start exactly on <<<image_1>>>. ${text}`;
  }
  if (imageIndex === phaseCount && phaseCount > 1) {
    const endToken = `<<<image_${phaseCount}>>>`;
    if (!new RegExp(`end matching ${endToken.replace(/[<>]/g, "\\$&")}`, "i").test(text)) {
      text = `${text} End matching ${endToken}.`;
    }
  }
  return text.replace(/\s+/g, " ").trim();
}

function injectCameraClause(prompt: string, move: CameraMoveId): string {
  const clause = expandCameraMove(move);
  if (prompt.toLowerCase().includes(clause.slice(0, 18).toLowerCase())) return prompt;
  if (move === "static" && /\bstatic\b/i.test(prompt)) return prompt;
  return `${prompt} Camera: ${clause}.`.replace(/\s+/g, " ").trim();
}

function injectPaceEnergy(
  prompt: string,
  pace: PaceId,
  energy: EnergyId,
): string {
  let text = prompt;
  const paceClause = expandPace(pace);
  const energyClause = expandEnergy(energy);
  if (pace !== "normal" && !text.toLowerCase().includes(paceClause.slice(0, 12).toLowerCase())) {
    text = `${text} Pace: ${paceClause}.`;
  }
  if (energy !== "normal" && !text.toLowerCase().includes(energyClause.slice(0, 12).toLowerCase())) {
    text = `${text} Subject energy: ${energyClause}.`;
  }
  return text.replace(/\s+/g, " ").trim();
}

export type PhaseAssertIssue = { field: string; message: string };

/**
 * `transformation`: the subject itself changes form (e.g. a sketch becoming a real building),
 * so the video beats may name the drawn stage.
 */
export function assertPhasePrompts(
  phase: FragmentadorPhase,
  phaseCount: number,
  opts?: { requireStill?: boolean; transformation?: boolean; allow?: PlanAllow },
): PhaseAssertIssue[] {
  const stylized = Boolean(opts?.allow?.stylized);
  const issues: PhaseAssertIssue[] = [];
  const requireStill = opts?.requireStill !== false && phase.role === "beat";

  if (phase.t_end <= phase.t_start) {
    issues.push({ field: "timing", message: "t_end deve ser maior que t_start" });
  }

  if (requireStill) {
    const still = phase.still_prompt_en.trim();
    if (still.length < STILL_PROMPT_MIN) {
      issues.push({ field: "still_prompt_en", message: `still_prompt_en muito curto (<${STILL_PROMPT_MIN})` });
    }
    if (still.length > STILL_PROMPT_MAX) {
      issues.push({ field: "still_prompt_en", message: `still_prompt_en muito longo (>${STILL_PROMPT_MAX})` });
    }
    if (STILL_CAMERA_VERBS.test(still)) {
      issues.push({
        field: "still_prompt_en",
        message: "still_prompt_en não deve descrever movimento de câmera (dolly/pan/tracking…)",
      });
    }
    if (!stylized && asksSketchStyle(still)) {
      issues.push({ field: "still_prompt_en", message: "still_prompt_en não pode pedir sketch/cartoon/low-poly" });
    }
  }

  const video = phase.video_prompt_en.trim();
  if (video.length < VIDEO_PROMPT_MIN) {
    issues.push({ field: "video_prompt_en", message: `video_prompt_en muito curto (<${VIDEO_PROMPT_MIN})` });
  }
  if (video.length > VIDEO_PROMPT_HARD_CAP) {
    issues.push({ field: "video_prompt_en", message: `video_prompt_en excede hard cap ${VIDEO_PROMPT_HARD_CAP}` });
  }
  const token = `<<<image_${phase.image_index}>>>`;
  if (!video.includes(token)) {
    issues.push({ field: "video_prompt_en", message: `video_prompt_en deve citar ${token}` });
  }
  if (!opts?.transformation && !stylized && asksSketchStyle(video)) {
    issues.push({ field: "video_prompt_en", message: "video_prompt_en não pode pedir sketch/cartoon/low-poly" });
  }
  if (phase.image_index < 1 || phase.image_index > phaseCount) {
    issues.push({ field: "image_index", message: `image_index fora de 1..${phaseCount}` });
  }

  return issues;
}

export function assertPlanTiming(phases: FragmentadorPhase[], totalDuration: number): PhaseAssertIssue[] {
  const issues: PhaseAssertIssue[] = [];
  if (phases.length < PHASE_COUNT_MIN || phases.length > PHASE_COUNT_MAX) {
    issues.push({
      field: "phases",
      message: `plano deve ter entre ${PHASE_COUNT_MIN} e ${PHASE_COUNT_MAX} fases`,
    });
  }
  for (let i = 0; i < phases.length; i++) {
    const p = phases[i];
    if (i > 0 && p.t_start !== phases[i - 1].t_end) {
      issues.push({
        field: "timing",
        message: `fase ${i + 1}: t_start (${p.t_start}) deve igualar t_end da anterior (${phases[i - 1].t_end})`,
      });
    }
  }
  if (phases.length) {
    if (phases[0].t_start !== 0) {
      issues.push({ field: "timing", message: "primeira fase deve começar em 0" });
    }
    const lastEnd = phases[phases.length - 1].t_end;
    if (lastEnd !== totalDuration) {
      issues.push({
        field: "timing",
        message: `soma das fases (${lastEnd}s) deve igualar total_duration (${totalDuration}s)`,
      });
    }
  }
  return issues;
}

function newPhaseId(i: number): string {
  return `phase_${i + 1}_${Math.random().toString(36).slice(2, 8)}`;
}

/** Normalize + repair Gemini output into a valid FragmentadorPlan. */
export function normalizeFragmentadorPlan(
  raw: unknown,
  opts: {
    durationSeconds: number;
    durationMin: number;
    durationMax: number;
    hasStart: boolean;
    hasEnd: boolean;
    phaseCount?: number;
  },
): FragmentadorPlan {
  const obj = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const look = obj.look === "cinematic" ? "cinematic" : "realistic";
  const intentRaw = String(obj.intent_hint || "");
  const intent_hint = (
    [
      "camera_showcase",
      "progressive_build",
      "subject_evolution",
      "cinematic_montage",
    ] as const
  ).includes(intentRaw as FragmentadorPlan["intent_hint"])
    ? (intentRaw as FragmentadorPlan["intent_hint"])
    : look === "cinematic"
    ? "cinematic_montage"
    : "camera_showcase";

  const wantedCount = opts.phaseCount
    ? clampPhaseCount(opts.phaseCount)
    : undefined;

  // Soft target only — Gemini chooses per-phase durations (and thus total).
  let totalHint = Math.round(Number(obj.total_duration_seconds) || opts.durationSeconds);
  totalHint = Math.max(opts.durationMin, Math.min(opts.durationMax, totalHint || opts.durationSeconds));

  const source = Array.isArray(obj.phases)
    ? obj.phases
    : Array.isArray(obj.storyboard)
    ? obj.storyboard
    : [];

  let phasesRaw = source.slice(0, PHASE_COUNT_MAX);
  if (wantedCount) {
    while (phasesRaw.length < wantedCount) {
      const i = phasesRaw.length;
      phasesRaw.push({
        label: `Fase ${i + 1}`,
        description: `Etapa ${i + 1}`,
        role: i === 0 ? "start" : i === wantedCount - 1 ? "end" : "beat",
      });
    }
    phasesRaw = phasesRaw.slice(0, wantedCount);
  } else if (phasesRaw.length < 2) {
    phasesRaw = [
      { label: "Início", description: "Abertura", role: "start" },
      { label: "Final", description: "Encerramento", role: "end" },
    ];
  }

  const n = phasesRaw.length;
  const requirements = normalizeRequirements(obj.requirements, n);
  const knownReqIds = new Set(requirements.map((r) => r.id));

  const phases: FragmentadorPhase[] = phasesRaw.map((p: any, i: number) => {
    const image_index = i + 1;
    let role: FragmentadorPhaseRole =
      i === 0 ? "start" : i === n - 1 ? "end" : "beat";
    if (p?.role === "start" || p?.role === "beat" || p?.role === "end") role = p.role;
    if (i === 0) role = "start";
    if (i === n - 1) role = "end";

    const camera_move = normalizeCameraMove(p?.camera_move || p?.camera || "steadicam");
    const transition_out = i === n - 1
      ? "camera"
      : normalizeTransitionOut(p?.transition_out || p?.transition || (intent_hint === "cinematic_montage" && i % 2 === 1 ? "cut" : "camera"));
    const pace = normalizePace(p?.pace || p?.tempo || "normal");
    const energy = normalizeEnergy(p?.energy || p?.mood || "normal");
    let still = String(p?.still_prompt_en || p?.still_prompt || "").trim();
    let video = String(p?.video_prompt_en || p?.prompt_en || p?.prompt || "").trim();

    if (!still && role === "beat") {
      still =
        `Photoreal ${i === 0 ? "wide" : "medium"} shot of the same subject and environment as the reference, ` +
        `clear framing, natural daylight, sharp materials, no illustration.`;
    }
    if (role === "start" && opts.hasStart) still = "";
    if (role === "end" && opts.hasEnd) still = "";

    if (!video) {
      video =
        `Medium shot of the scene matching <<<image_${image_index}>>>, clear subject action, ` +
        `natural soft light, photoreal cinematic look.`;
    }

    // "Start exactly from" may only cite this phase's own image.
    video = video
      .replace(/@image_(\d+)/gi, "<<<image_$1>>>")
      .replace(/\bstart exactly (?:on|from) <<<image_(\d+)>>>\.?\s*/gi, (m, k) => (Number(k) === image_index ? m : ""));
    video = ensureStartEndCites(video, image_index, n);
    // With requirements the planner's camera prose already names the move and pace; generic tags would repeat it.
    if (!requirements.length) {
      video = injectCameraClause(video, camera_move);
      video = injectPaceEnergy(video, pace, energy);
    }
    if (transition_out === "cut" && i < n - 1) {
      if (!/hard cut/i.test(video)) {
        video = `${video} End this beat ready for a HARD CUT.`;
      }
    } else if (i < n - 1 && !requirements.length && !/without cutting|continuous/i.test(video)) {
      video = `${video} Carry into the next beat without cutting.`;
    }
    // With requirements the whole prompt is condensed later by create-video (never cut per phase).
    const videoBudget = requirements.length ? VIDEO_PROMPT_HARD_CAP : phaseVideoPromptBudget(n);
    if (video.length > videoBudget) {
      video = truncateAtBoundary(video, videoBudget);
      video = ensureCite(video, image_index);
    }
    if (video.length > VIDEO_PROMPT_HARD_CAP) {
      video = truncateAtBoundary(video, VIDEO_PROMPT_HARD_CAP);
    }
    if (still.length > STILL_PROMPT_MAX) still = truncateText(still, STILL_PROMPT_MAX);

    const description_pt = truncateText(
      String(p?.description_pt || p?.description || p?.label || `Fase ${i + 1}`),
      DESCRIPTION_PT_MAX,
    );

    const reuseRaw = Number(p?.reuse_from_index ?? p?.reuse_image_from ?? p?.keep_image_from);
    const reuse_from_index =
      Number.isFinite(reuseRaw) && reuseRaw >= 1 && reuseRaw <= PHASE_COUNT_MAX
        ? Math.round(reuseRaw)
        : null;

    const action_en = cleanLine(p?.action_en || p?.action, ACTION_EN_MAX);
    const on_screen_text = normalizeOnScreenText(p?.on_screen_text);
    const requirement_ids = (Array.isArray(p?.requirement_ids) ? p.requirement_ids : [])
      .map((x: unknown) => String(x || "").trim())
      .filter((x: string) => knownReqIds.has(x));

    return {
      id: String(p?.id || newPhaseId(i)),
      label: String(p?.label || `Fase ${i + 1}`).slice(0, 48),
      role,
      t_start: 0,
      t_end: 1,
      camera_move,
      transition_out,
      pace,
      energy,
      description_pt,
      still_prompt_en: still,
      video_prompt_en: video,
      image_index,
      reuse_from_index,
      ...(action_en ? { action_en } : {}),
      ...(on_screen_text.length ? { on_screen_text } : {}),
      ...(requirement_ids.length ? { requirement_ids: Array.from(new Set<string>(requirement_ids)) } : {}),
    };
  });

  // Prefer Gemini's per-phase duration_seconds / t_start–t_end; fallback to pace weights.
  const geminiDurs = phasesRaw.map((p: any) => {
    const explicit = Number(p?.duration_seconds ?? p?.duration);
    if (Number.isFinite(explicit) && explicit >= 1) return Math.min(12, Math.round(explicit));
    const span = Math.round(Number(p?.t_end) - Number(p?.t_start));
    if (Number.isFinite(span) && span >= 1) return Math.min(12, span);
    return null;
  });
  const allGemini = geminiDurs.every((d: number | null) => d != null);

  let total: number;
  if (allGemini) {
    const durs = geminiDurs as number[];
    let sum = durs.reduce((a, b) => a + b, 0);
    if (sum < opts.durationMin || sum > opts.durationMax) {
      // Scale to fit model limits while keeping relative lengths
      const target = Math.max(opts.durationMin, Math.min(opts.durationMax, sum || totalHint));
      const scale = target / (sum || 1);
      let t = 0;
      for (let i = 0; i < phases.length; i++) {
        const dur = i === phases.length - 1
          ? Math.max(1, target - t)
          : Math.max(1, Math.round(durs[i] * scale));
        phases[i].t_start = t;
        phases[i].t_end = t + dur;
        t += dur;
      }
      if (phases.length) phases[phases.length - 1].t_end = target;
      total = target;
    } else {
      let t = 0;
      for (let i = 0; i < phases.length; i++) {
        const dur = durs[i];
        phases[i].t_start = t;
        phases[i].t_end = t + dur;
        t += dur;
      }
      total = t;
    }
  } else {
    total = totalHint;
    const weights = phases.map((p) => PACE_WEIGHT[p.pace] || 1);
    const weightSum = weights.reduce((a, b) => a + b, 0) || phases.length;
    let t = 0;
    for (let i = 0; i < phases.length; i++) {
      const share = weights[i] / weightSum;
      const dur = i === phases.length - 1
        ? Math.max(1, total - t)
        : Math.max(1, Math.round(total * share));
      phases[i].t_start = t;
      phases[i].t_end = t + dur;
      t += dur;
    }
    if (phases.length) phases[phases.length - 1].t_end = total;
  }

  const style_en = cleanLine(obj.style_en, STYLE_EN_MAX);
  const continuity_en = cleanLine(obj.continuity_en, CONTINUITY_EN_MAX);
  const avoid_en = cleanLine(obj.avoid_en, AVOID_EN_MAX);
  const allow = normalizeAllow(obj.allow);
  if (phases.some((p) => p.on_screen_text?.length)) allow.text_graphics = true;

  return {
    total_duration_seconds: total,
    look,
    intent_hint,
    phases,
    ...(requirements.length ? { requirements } : {}),
    ...(style_en ? { style_en } : {}),
    ...(continuity_en ? { continuity_en } : {}),
    ...(avoid_en ? { avoid_en } : {}),
    allow,
  };
}

const RAW_MOVE_ID = /\b(dolly|crane|pan|tilt|orbit)_(in|out|down|up|left|right)\b/gi;

export function buildVideoStoryboard(phases: FragmentadorPhase[]): Array<{
  label: string;
  description: string;
  prompt_en: string;
  image_index: number;
  transition_out?: TransitionOutId;
  pace?: PaceId;
  energy?: EnergyId;
  camera_move?: CameraMoveId;
}> {
  return phases.map((p) => ({
    label: p.label,
    description: p.description_pt,
    prompt_en: truncateAtBoundary(
      [
        p.action_en ? `${p.action_en.replace(/[.\s]+$/, "")}.` : "",
        p.video_prompt_en.replace(RAW_MOVE_ID, "$1 $2"),
        onScreenTextClause(p.on_screen_text),
      ].filter(Boolean).join(" "),
      VIDEO_PROMPT_HARD_CAP,
    ),
    image_index: p.image_index,
    transition_out: p.transition_out,
    pace: p.pace,
    energy: p.energy,
    camera_move: p.camera_move,
  }));
}

function formatTimecode(seconds: number): string {
  const s = Math.max(0, Math.round(Number(seconds) || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}

const TRANSFORMATION_WORDS =
  /\b(transform|morph|turns? into|becomes?|evolv|materializ|grows? into|dissolves? into|rises? into|gains? volume)/i;

/** Subject changes between checkpoints (evolution/build/morph) — not a pure camera reveal. */
export function isTransformationPlan(
  intentHint: FragmentadorPlan["intent_hint"] | string | undefined,
  phases: Array<{ video_prompt_en?: string }>,
): boolean {
  if (intentHint === "progressive_build" || intentHint === "subject_evolution") return true;
  if (intentHint === "cinematic_montage") return false;
  return phases.some((p) => TRANSFORMATION_WORDS.test(String(p.video_prompt_en || "")));
}

const IMAGE_TOKEN = /<<<image_(\d+)>>>/gi;

/** A beat may only cite its own checkpoint or the next one; planners sometimes number by user photo instead of scene. */
function remapStrayImageTokens(text: string, own: number): string {
  return text.replace(IMAGE_TOKEN, (m, k) => {
    const n = Number(k);
    return n === own || n === own + 1 ? m : `<<<image_${own}>>>`;
  });
}

/** The clip ends here (the cut happens at the stitch), so a trailing "before a hard cut" only invites an in-clip cut. */
const TRAILING_CUT_WORDS = /,?\s*(?:holding briefly\s+)?before (?:a|the) hard cut/gi;

/**
 * One-image parts open locked on that image; asking to also end on it cancels the camera move.
 * Matches "ending on <<<image_1>>>", "decelerating into the exact composition of <<<image_1>>>", "End matching <<<image_1>>>.".
 */
const SAME_IMAGE_END_LOCK =
  /,?\s*(?:(?:ending|landing|settling|finishing) on|decelerating into (?:the )?(?:exact )?(?:composition|framing) of|end(?:ing)? matching)\s+<<<image_1>>>\.?/gi;

const MID_HOLD_WORDS =
  /\b(?:hold(?:s|ing)? (?:steady|still|the frame)|(?:locked(?:-off)? )?static camera(?:, no pan or dolly)?|freeze(?:s)? on)\b/gi;

export function onScreenTextClause(list?: string[]): string {
  const items = (list || []).map((s) => s.trim()).filter(Boolean);
  if (!items.length) return "";
  return `On-screen text, exactly: ${items.map((s) => `"${s.replace(/"/g, "'")}"`).join(", ")}.`;
}

export type ContinuousPromptOpts = {
  transformation?: boolean;
  /** Plan built from the user's requests: every scene carries its full action (condensed later, never cut here). */
  requirements?: PlanRequirement[];
  style_en?: string;
  continuity_en?: string;
  avoid_en?: string;
  allow?: PlanAllow;
};

function sentence(text: string | undefined): string {
  const t = String(text || "").replace(/\s+/g, " ").trim();
  if (!t) return "";
  return /[.!?]$/.test(t) ? t : `${t}.`;
}

export function buildContinuousPrompt(
  phases: FragmentadorPhase[],
  opts?: ContinuousPromptOpts,
): string {
  if (opts?.requirements?.length) return buildRequestedPrompt(phases, opts);
  const hasCuts = phases.some((p, i) => i < phases.length - 1 && p.transition_out === "cut");
  const transformation = Boolean(opts?.transformation);
  const n = phases.length;
  const lastIdx = Math.max(1, n);

  const trajectory = phases.map((p, i) => `<<<image_${p.image_index || i + 1}>>>`).join("→");
  const opener = hasCuts
    ? `Photoreal video anchored to the uploaded images, HARD CUTS only where marked; continuous physical camera motion inside each beat — no slideshow.`
    : transformation
    ? `ONE continuous shot: the subject physically transforms through ordered checkpoints ${trajectory} while the camera keeps moving (natural parallax).`
    : n === 1
    ? `ONE continuous shot opening exactly on <<<image_1>>>; only the camera moves (natural parallax), revealing more of the same place.`
    : `ONE continuous shot of the same subject and location, only the camera moves (natural parallax): <<<image_1>>> → camera reveals → <<<image_${lastIdx}>>>.`;
  const closer = hasCuts
    ? `Inside each beat: no morphing, no dissolve, no teleportation, no visible drone.`
    : transformation
    ? `Each checkpoint matches its reference when reached; transform continuously between them. No cuts, no dissolve, no crossfade, no new text, no visible drone.`
    : `Preserve identity, proportions, materials and surroundings. No cuts, no morphing, no dissolve, no teleportation, no visible drone.`;

  const phaseBudget = Math.max(
    CONTINUOUS_PHASE_MIN,
    Math.floor((CONTINUOUS_PROMPT_MAX - opener.length - closer.length - n) / Math.max(1, n)),
  );

  const parts = phases.map((p, i) => {
    const span = `${formatTimecode(p.t_start)}–${formatTimecode(p.t_end)}`;
    const next = phases[i + 1];
    const isFirst = i === 0;
    const isLast = !next;
    const own = p.image_index || i + 1;
    const cite = `<<<image_${own}>>>`;
    const flowsOn = !isLast && p.transition_out !== "cut";
    const cam = flowsOn && p.camera_move === "static"
      ? "slow push bridging to the next framing"
      : expandCameraMove(p.camera_move);
    let prose = remapStrayImageTokens(p.video_prompt_en, own).replace(RAW_MOVE_ID, "$1 $2");
    if (flowsOn) {
      prose = prose.replace(MID_HOLD_WORDS, "drifting smoothly").replace(/\bexact (composition|perspective|framing)\b/gi, "$1");
    }
    if (isLast) prose = prose.replace(TRAILING_CUT_WORDS, "");
    if (n === 1) {
      prose = prose
        .replace(SAME_IMAGE_END_LOCK, (m) => (m.trim().endsWith(".") ? "." : ""))
        .replace(/\.(\s*\.)+/g, ".");
    }
    const alreadyOpens = isFirst && /^\s*start exactly (?:on|from)\b/i.test(prose);
    const head = isFirst
      ? alreadyOpens ? "" : `Start exactly from ${cite}`
      : isLast
      ? `Land exactly on <<<image_${lastIdx}>>>`
      : transformation
      ? `Continue through checkpoint ${cite}`
      : `Continue uninterrupted`;
    const bridge = isLast
      ? "Finish on a stable hero view."
      : p.transition_out === "cut"
      ? `HARD CUT to ${next.label}.`
      : /without cutting|continuous/i.test(prose) ? "" : "No cut.";
    const camPart = /camera:/i.test(prose) ? "" : ` Camera: ${cam}.`;
    const prefix = `${span} — ${head ? `${head}. ` : ""}`;
    const suffix = bridge ? ` ${bridge}` : "";
    const room = Math.max(40, phaseBudget - prefix.length - suffix.length);
    const body = truncateAtBoundary(`${prose}${camPart}`, room);
    return `${prefix}${body}${suffix}`;
  });

  return [opener, ...parts, closer].join(" ");
}

/**
 * Drops the compiler's own per-phase tags (camera/pace/energy labels, bridge, look cite already in the
 * scene head) that plans saved before requirements existed still carry.
 */
function stripGeneratedTags(prose: string, headCite: string): string {
  let t = prose
    .replace(/\s*\b(?:Camera|Pace|Subject energy):\s[^.]*\./g, "")
    .replace(/\s*Carry into the next beat without cutting\./gi, "");
  if (headCite) {
    t = t.replace(new RegExp(`\\s*Match the look of ${headCite.replace(/[<>]/g, "\\$&")}\\.`, "g"), "");
  }
  return t.replace(/\s+/g, " ").trim();
}

/** "The drone rises" / "The FPV drone rises" reads as a drone in frame; with no aircraft allowed, the drone is the camera. */
function cameraNotDrone(text: string): string {
  const keep = "shot|view|footage|flight|tour|style|aesthetic";
  const asCamera = (article: string, possessive?: string) =>
    `${/^[A-Z]/.test(article) ? "The" : "the"} camera${possessive || ""}`;
  return text
    .replace(
      new RegExp(`\\b(a|an|the)\\s+(?:fpv\\s+)?drone('s)?(?!\\s+(?:${keep}))\\b`, "gi"),
      (_m, article: string, possessive?: string) => asCamera(article, possessive),
    )
    .replace(
      new RegExp(`\\bFPV\\s+drone('s)?(?!\\s+(?:${keep}))\\b`, "g"),
      (_m, possessive?: string) => `the camera${possessive || ""}`,
    );
}

/** Each scene = timecode + what happens + camera + exact on-screen text; global style/continuity/avoid from the user. */
function buildRequestedPrompt(phases: FragmentadorPhase[], opts: ContinuousPromptOpts): string {
  const hasCuts = phases.some((p, i) => i < phases.length - 1 && p.transition_out === "cut");
  const transformation = Boolean(opts.transformation);
  const allow = opts.allow;
  const n = phases.length;
  const lastIdx = Math.max(1, n);
  const trajectory = phases.map((p, i) => `<<<image_${p.image_index || i + 1}>>>`).join("→");

  const opener = hasCuts
    ? `Video anchored to the uploaded images, HARD CUTS only where marked; continuous physical camera motion inside each beat — no slideshow.`
    : transformation
    ? `ONE continuous shot: the subject physically transforms through ordered checkpoints ${trajectory} while the camera keeps moving.`
    : `ONE continuous shot anchored to the uploaded images, following the scenes below in order.`;

  const parts = phases.map((p, i) => {
    const span = `${formatTimecode(p.t_start)}–${formatTimecode(p.t_end)}`;
    const next = phases[i + 1];
    const isFirst = i === 0;
    const isLast = !next;
    const own = p.image_index || i + 1;
    const cite = `<<<image_${own}>>>`;
    const flowsOn = !isLast && p.transition_out !== "cut";
    const cam = flowsOn && p.camera_move === "static"
      ? "slow push bridging to the next framing"
      : expandCameraMove(p.camera_move);
    let prose = remapStrayImageTokens(p.video_prompt_en, own).replace(RAW_MOVE_ID, "$1 $2");
    if (flowsOn) {
      prose = prose.replace(MID_HOLD_WORDS, "drifting smoothly").replace(/\bexact (composition|perspective|framing)\b/gi, "$1");
    }
    if (isLast) prose = prose.replace(TRAILING_CUT_WORDS, "");
    if (n === 1) {
      prose = prose
        .replace(SAME_IMAGE_END_LOCK, (m) => (m.trim().endsWith(".") ? "." : ""))
        .replace(/\.(\s*\.)+/g, ".");
    }
    const alreadyOpens = isFirst && /^\s*start exactly (?:on|from)\b/i.test(prose);
    const head = isFirst
      ? alreadyOpens ? "" : `Start exactly from ${cite}.`
      : isLast
      ? `Land on <<<image_${lastIdx}>>>.`
      : transformation
      ? `Continue through checkpoint ${cite}.`
      : "";
    const texts = (p.on_screen_text || []).map((s) => s.trim()).filter(Boolean);
    let action = sentence(remapStrayImageTokens(p.action_en || "", own));
    if (!allow?.aircraft) {
      action = cameraNotDrone(action);
      prose = cameraNotDrone(prose);
    }
    // Texts the action already names are quoted in place (keeps their role: headline, CTA…) and not listed twice.
    const inAction = texts.filter((s) => {
      const quoted = `"${s.replace(/"/g, "'")}"`;
      for (const q of [`'${s}'`, `“${s}”`, `‘${s}’`]) {
        if (action.includes(q)) action = action.split(q).join(quoted);
      }
      return action.includes(quoted);
    });
    const listed = texts.filter((s) => !inAction.includes(s));
    const textClause = [
      onScreenTextClause(listed),
      inAction.length ? "Render every quoted on-screen text exactly as written." : "",
    ].filter(Boolean).join(" ");
    prose = stripGeneratedTags(prose, head ? cite : "");
    const camPart = !prose ? ` Camera: ${cam}.` : "";
    const bridge = isLast
      ? ""
      : p.transition_out === "cut"
      ? `HARD CUT to ${next.label}.`
      : /without cutting|continuous/i.test(prose) ? "" : "No cut.";
    return [`${span} —`, head, action, sentence(`${prose}${camPart}`.trim()), textClause, bridge]
      .filter(Boolean)
      .join(" ");
  });

  const noText = allow?.text_graphics ? "no text other than the quoted on-screen text" : "no text overlay";
  // Allowed aircraft are in the scene; the drone carrying the camera never is.
  const noDrone = allow?.aircraft ? ", no visible camera drone" : ", no visible drone";
  const closer = hasCuts
    ? `Inside each beat: no dissolve, no teleportation${noDrone}; ${noText}.`
    : transformation
    ? `Each checkpoint matches its reference when reached; transform continuously between them. No cuts, no dissolve, no crossfade${noDrone}; ${noText}.`
    : `Preserve the identity, proportions and materials of the pictured subject. No cuts, no morphing, no dissolve, no teleportation${noDrone}; ${noText}.`;

  return [
    opener,
    opts.style_en ? `Style: ${sentence(opts.style_en)}` : "",
    opts.continuity_en ? `Continuity: ${sentence(opts.continuity_en)}` : "",
    ...parts,
    closer,
    opts.avoid_en ? `Avoid: ${sentence(opts.avoid_en)}` : "",
  ]
    .filter(Boolean)
    .join(" ");
}

export function buildStillPrompt(phase: FragmentadorPhase): string {
  const base = phase.still_prompt_en.trim();
  if (!base) return "";
  if (/photoreal/i.test(base) && /no sketch/i.test(base)) return base;
  return `${base}${STILL_PROMPT_SUFFIX}`.replace(/\s+/g, " ").trim();
}

export function fragmentadorSystemPrompt(phaseCount?: number): string {
  const n = phaseCount ? clampPhaseCount(phaseCount) : null;
  const phaseRule = n
    ? `EXACTLY ${n} phases (no more, no less). YOU choose duration_seconds (integer, typically 1–6) for EACH phase based on what the beat needs (hold longer for slow/impact, shorter for fast cuts). total_duration_seconds MUST equal the sum of phase durations and stay within 4–30s.`
    : `${PHASE_COUNT_MIN} to ${PHASE_COUNT_MAX} phases. YOU choose duration_seconds per phase and total_duration_seconds (= sum), within 4–30s.`;

  return `You are a director planning AI video (Kling / Seedance / Wan) for the SEU.VIDEO Fragmentador.
Respond with JSON ONLY. No markdown. Strict RFC 8259 JSON: double-quoted keys/strings, no trailing commas, no comments.

(See VIDEO_PROMPT_GUIDE — shot type, camera, lighting, style.)

=== USER REQUESTS ARE BINDING ===
- The message lists USER REQUESTS: everything the user asked for this video, oldest first, verbatim. They are the source of truth and override every default below. When two requests conflict, the NEWER one wins; everything else from older requests still applies.
- Split them into atomic "requirements": each distinct thing asked (subject, action, object, effect, lens/camera, lighting/time of day, color, graphics/typography/logo, exact texts, order, mood, ending/CTA, things to avoid). Keep numbers, names and quoted strings. Never drop or silently merge a request away.
- requirements: [{"id":"r1","text_pt":"<one faithful Portuguese line>","scope":"global" or [1-based phase numbers]}].
- EVERY requirement must be realized in the plan: global ones in style_en / continuity_en / avoid_en (or in every relevant phase), scoped ones in those phases' action_en / on_screen_text / video_prompt_en. Each phase lists the ids it realizes in requirement_ids.
- action_en (English, ≤${ACTION_EN_MAX} chars): what HAPPENS in frame during this phase — subjects, objects, effects, graphics animating, how elements enter/assemble — no camera motion, and never the drone/camera itself. "" only when nothing but the camera moves.
- on_screen_text: array of the exact strings that must appear on screen in this phase (headline, CTA, labels), copied verbatim in the user's language; [] when none.
- style_en (≤${STYLE_EN_MAX} chars): look, lens, lighting, time of day, color grade the user asked for. "" = default photoreal.
- continuity_en (≤${CONTINUITY_EN_MAX} chars): what must stay consistent as the user asked (one take, same subject, no morphing…). "" when nothing specific.
- avoid_en (≤${AVOID_EN_MAX} chars): things the user said NOT to show or do, English, comma separated. "" when none.
- allow: {"text_graphics": true when the user wants any on-screen text/graphics/logo/typography, "aircraft": true when the user wants planes/helicopters/drones visible in frame (a "drone tour", drone shot or FPV flight is the camera, not an aircraft in frame: keep false), "stylized": true when the user wants sketch/cartoon/anime/illustration/low-poly}. Otherwise false.

=== FRAGMENTADOR CONTRACT ===
- ${phaseRule}
- Domain-agnostic: works for products, people, interiors, cars, food, nature, architecture, fashion — infer subject from the brief + photos, NEVER assume buildings unless the brief/photos show them.
- Each phase needs BOTH still_prompt_en AND video_prompt_en (English).
- still_prompt_en: photoreal STILL only — composition, angle, subject, light. NO camera motion verbs (dolly/pan/tracking/shake).
- When start+end photos exist:
  - camera_showcase: SAME finished subject/place at different camera distances/angles (e.g. product detail → full product; room corner → full room). Mid stills are intermediate viewpoints of that same subject — never invent a different identity.
  - progressive_build / subject_evolution: SAME subject evolving in time (assembly, staging, before→after, growth). Mid stills MUST look LESS complete / earlier than the end photo; finished look only on the last phase.
- video_prompt_en: describe ONE physical camera beat only (what happens in frame goes in action_en) — what the camera does in this second range (pull back, rise, orbit, accelerate/decelerate, reveal). Call it "the camera", never "the drone": a drone named in the prompt gets drawn in frame. Cite <<<image_N>>> where N = the SCENE/PHASE number (1-based), never the user photo number: phase K cites only <<<image_K>>> (and optionally <<<image_K+1>>> as where it heads). Prefer language like: "Continue pulling physically backward…", "Accelerate the orbital flight…".
- First phase MUST start with "Start exactly from <<<image_1>>>".
- Last phase MUST end matching <<<image_N>>> (exact composition of the end reference).
- Continuous take (transition_out "camera"): middle phases PASS THROUGH their checkpoint while moving — never "hold steady", "static", "freeze" or "exact composition" before the last phase. When framing/scale differs between checkpoints, the camera must physically travel (push-in / pull-back / rise) to bridge it — no jumps in scale.
- video_prompt_en is natural English prose: never write camera_move ids (dolly_in, crane_down…) or "Camera:"/"Pace:" labels — the compiler appends those.
- video_prompt_en length 100–220 characters, camera only. Dense is better; the user's content lives in action_en / on_screen_text / style_en.
- description_pt: Portuguese UI summary, max 80 chars.
- camera_move MUST be one of: ${CAMERA_MOVE_IDS.join(", ")}. It MUST agree with the direction in the brief and video_prompt_en: toward/into/até a janela → dolly_in; away/revealing wider → dolly_out; rising → crane; descending/drone lowering → crane_down; lateral → tracking or pan.
- transition_out: "camera" (continuous take) OR "cut". For drone / reveal / adrenaline / continuous flight briefs: ALL transitions "camera". Use "cut" only when the user asks for montage/hard cuts (e.g. "corte para…"). On ADJUST keep the current transition_out values unless the user asks about cuts/continuity.
- pace: slow | normal | fast | impact — mix fast/impact with slow for adrenaline speed ramps.
- energy: calm | normal | action | intense.
- Default (only when the user did not ask otherwise): photoreal, no sketch/low-poly/cartoon/anime, no text overlay, no aircraft in frame. When the user asks for any of these, set allow accordingly and plan them.
- Do NOT redesign identity in the photos — only camera path (and, if progressive_build/subject_evolution, credible temporal progress matching the brief).
- intent_hint: camera_showcase (same subject, camera reveal) | progressive_build (assembly/staging/before-after over time) | subject_evolution (the subject transforms/morphs from one form into another, e.g. symbol → map → real place) | cinematic_montage.
- look: cinematic | realistic
- When user sends an ADJUST instruction: you MAY change phase count between ${PHASE_COUNT_MIN} and ${PHASE_COUNT_MAX} if they ask for more/fewer scenes/phases/beats (e.g. "mais fases", "adicione cenas"); otherwise keep the current count. Preserve start/end photo anchors. Update only the fields the request concerns among transition_out, pace, energy, camera_move, duration_seconds, still_prompt_en (new mids), video_prompt_en, action_en, on_screen_text (e.g. "voo contínuo" → all transition_out=camera; "mais rápido" → shorter duration + pace=fast).
- On ADJUST only: for EACH phase set reuse_from_index to the 1-based index of a CURRENT PLAN phase whose still should be KEPT (same beat/framing), or null if a new still must be generated. Prefer reusing existing mids that still match to avoid wasting image credits. First phase → reuse_from_index=1 when a start photo exists; last phase → reuse the previous last index when an end photo exists.

EXAMPLE video_prompt_en beats (style to imitate — adapt subject to the brief; do not copy architecture wording if the subject is not a building):
- Detail→full subject reveal: "Start exactly from <<<image_1>>> tight on the detail; smooth backward rise with natural parallax."
- "Continue pulling back; subtle orbital arc; reveal more of the same subject."
- "Accelerate pull-back; keep exact identity, materials and proportions."
- Last phase only: "Decelerate into exact composition of <<<image_N>>>; stable hero framing."

JSON shape:
{
  "total_duration_seconds": 10,
  "look": "realistic",
  "intent_hint": "camera_showcase",
  "requirements": [{ "id": "r1", "text_pt": "...", "scope": "global" }],
  "style_en": "",
  "continuity_en": "",
  "avoid_en": "",
  "allow": { "text_graphics": false, "aircraft": false, "stylized": false },
  "phases": [
    {
      "label": "Abertura",
      "role": "start",
      "duration_seconds": 3,
      "camera_move": "steadicam",
      "transition_out": "camera",
      "pace": "normal",
      "energy": "normal",
      "description_pt": "...",
      "still_prompt_en": "",
      "video_prompt_en": "...",
      "action_en": "",
      "on_screen_text": [],
      "requirement_ids": ["r1"],
      "reuse_from_index": null
    }
  ]
}`;
}
